/**
 * Header.tsx
 * 
 * Responsive header with:
 * - Logo + OS_ONLINE status on left
 * - Desktop: horizontal nav links
 * - Mobile: hamburger menu with slide-out overlay
 * - Auto-hides on scroll down, reappears on scroll up
 */

import { useState } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

// Navigation items — 'Home' removed since logo links to /
const navItems = [
    { name: 'About', href: '/aboutme' },
    { name: 'Timeline', href: '/timeline' },
    { name: 'Architecture', href: '/architecture' },
    { name: 'Badges', href: '/achievements' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Games', href: '/games' },
    { name: 'Dashboard', href: '/dashboard' },
    { name: 'Nexus', href: '/nexus' },
    { name: 'Contact', href: '/contact' },
];

export function Header() {
    const { scrollY } = useScroll();
    const [hidden, setHidden] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useMotionValueEvent(scrollY, "change", (latest) => {
        const previous = scrollY.getPrevious();
        if (previous !== undefined && latest > previous && latest > 150) {
            setHidden(true);
        } else {
            setHidden(false);
        }
    });

    // Close mobile menu on route change
    const handleNavClick = () => {
        setMobileMenuOpen(false);
    };

    return (
        <>
            <motion.header
                variants={{
                    visible: { y: 0 },
                    hidden: { y: "-100%" },
                }}
                animate={hidden ? "hidden" : "visible"}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 md:px-8 backdrop-blur-md bg-black/70 border-b border-white/5"
            >
                {/* Left: Logo + Status */}
                <div className="flex items-center gap-4 shrink-0">
                    <Link to="/" className="text-xl font-bold tracking-tighter text-white hover:text-emerald-400 transition-colors">
                        PARAS LEHANA
                    </Link>
                    <div className="flex items-center gap-2 px-2 py-1 bg-emerald-500/10 rounded border border-emerald-500/20">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        <span className="text-[10px] font-mono text-emerald-500/70 tracking-widest hidden sm:inline">OS_ONLINE</span>
                    </div>
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden lg:flex gap-6 items-center flex-1 justify-end">
                    {navItems.map((item) => (
                        <Link
                            key={item.name}
                            to={item.href}
                            className={`text-xs font-medium uppercase tracking-wider transition-colors ${location.pathname === item.href ? 'text-emerald-400' : 'text-gray-400 hover:text-emerald-400'}`}
                        >
                            {item.name}
                        </Link>
                    ))}
                </nav>

                {/* Mobile Hamburger Button */}
                <button
                    className="lg:hidden flex flex-col items-center justify-center w-10 h-10 gap-1.5 relative z-[60]"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label="Toggle navigation menu"
                >
                    <motion.span
                        animate={mobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                        className="w-6 h-0.5 bg-emerald-400 block transition-colors"
                    />
                    <motion.span
                        animate={mobileMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                        className="w-6 h-0.5 bg-emerald-400 block"
                    />
                    <motion.span
                        animate={mobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                        className="w-6 h-0.5 bg-emerald-400 block"
                    />
                </button>
            </motion.header>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center"
                    >
                        <nav className="flex flex-col items-center gap-6">
                            {navItems.map((item, index) => (
                                <motion.div
                                    key={item.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ delay: index * 0.05, duration: 0.3 }}
                                >
                                    <Link
                                        to={item.href}
                                        onClick={handleNavClick}
                                        className={`text-2xl font-bold uppercase tracking-wider transition-colors ${location.pathname === item.href
                                                ? 'text-emerald-400'
                                                : 'text-gray-300 hover:text-emerald-400'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>

                        {/* Mobile status footer */}
                        <div className="absolute bottom-12 flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded border border-emerald-500/20">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            <span className="text-[10px] font-mono text-emerald-500/70 tracking-widest">OS_ONLINE</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
